import { useCallback, useEffect, useMemo, useState } from "react";
import { generateId } from "@evergis/api";
import { ComparisonOperator } from "@evergis/ui";

import { FilterCondition } from "../../../evergis/core/condition/FilterCondition";
import { getAttributeType } from "../../../evergis/core/layer/getAttribute";
import { useAttributes } from "../../../hooks/attributes/useAttributes";
import { usePropAsState } from "../../../hooks/usePropAsState";
import { useFilterConditionOperator } from "./useFilterConditionOperator";

import { LayerInfo } from "../../../interfaces/resourceInfo";

import {
  FilterConditionType,
  FilterConditionState,
  ConditionOperationUpdate,
  AddCondition,
  DeleteCondition,
  UpdateCondition,
  SetConditions,
  FilterConditionOperation,
} from "../types";

export interface FilterConditionUpdate extends ConditionOperationUpdate {
  addCondition: AddCondition;
  deleteCondition: DeleteCondition;
  updateCondition: UpdateCondition;
  setConditions: SetConditions;
  setRawCondition: (condition: string) => void;
}

export const useLayerCondition = (layer: LayerInfo): [FilterConditionState, FilterConditionUpdate] => {
  const { condition } = layer;
  const filterCondition = useMemo(() => new FilterCondition(condition), [condition]);
  const [rawCondition, setRawCondition] = usePropAsState(condition || null);
  const [conditions, update] = useAttributes<FilterConditionType | undefined>(undefined);
  const [conditionOperation, { toggleConditionOperation, setConditionOperation }] = useFilterConditionOperator();
  const [parsingError, setParsingError] = useState(false);

  const setConditions = useCallback(
    (value: FilterConditionType[]) => {
      update.setAttributes(extendConditions(value, layer));
    },
    [layer, update],
  );

  useEffect(() => {
    if (filterCondition.isParsingError()) {
      setParsingError(true);

      return;
    }

    if (filterCondition.toString()) {
      setConditions(filterCondition.conditions);
      setConditionOperation(filterCondition.conditionOperation || FilterConditionOperation.AND);
    }
  }, [filterCondition.toString()]); // eslint-disable-line

  useEffect(() => {
    parsingError && setParsingError(false);
  }, [(conditions || []).length]); // eslint-disable-line

  return [
    { rawCondition, conditions, conditionOperation, parsingError },
    {
      addCondition: update.addAttribute,
      deleteCondition: update.deleteAttribute,
      updateCondition: update.updateAttribute,
      setConditions,
      toggleConditionOperation,
      setConditionOperation,
      setRawCondition,
    },
  ];
};

function extendConditions(conditions: FilterConditionType[], layer?: LayerInfo) {
  if (!layer) {
    return conditions;
  }

  return conditions.map(condition => ({
    ...convertCondition(condition),
    type: getAttributeType(layer, condition.attribute),
    id: generateId(),
  }));
}

function convertCondition(condition: FilterConditionType) {
  if (condition.comparisonOperator === ComparisonOperator.Equals && condition.value === null) {
    return {
      ...condition,
      comparisonOperator: ComparisonOperator.Empty,
    };
  }

  if (condition.comparisonOperator === ComparisonOperator.NotEquals && condition.value === null) {
    return {
      ...condition,
      comparisonOperator: ComparisonOperator.Filled,
    };
  }

  return condition;
}
